<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('dailyCashAdvance.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('dailyCashAdvance.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width: 200px"
              @change="fetchData()"
            />
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-model="search"
              v-tooltip="$t('dailyCashAdvance.search')"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('dailyCashAdvance.branch') }}</label>
                  <ts-branch-filter
                    @filter="branchFilter"
                    class="ts-w-full"
                  ></ts-branch-filter>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="ts-overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :rowGroup="{
            field: 'advance_date'
          }"
        >
          <template v-slot="{ record }">
            <td>{{ record.branch.branch_name_en }}</td>
            <td>{{ record.advance_by.employee_name_en }}</td>
            <td>{{ record.purpose }}</td>
            <td>
              {{
                record.payment_method
                  ? record.payment_method.payment_method
                  : ''
              }}
            </td>
            <td
              class="ts-text-right ts-text-blue-700 ts-font-semibold ts-whitespace-no-wrap"
            >
              {{
                record.advance_amount
                  ? record.currency.currency_code +
                    ' ' +
                    $formatNumber(record.advance_amount, 2)
                  : 0
              }}
            </td>
            <td
              class="ts-text-right ts-text-blue-700 ts-font-semibold ts-whitespace-no-wrap"
            >
              {{
                record.amount_used
                  ? record.currency.currency_code +
                    ' ' +
                    $formatNumber(record.amount_used, 2)
                  : 0
              }}
            </td>
            <td
              class="ts-text-right ts-text-blue-700 ts-font-semibold ts-whitespace-no-wrap"
            >
              {{
                record.amount_due
                  ? record.currency.currency_code +
                    ' ' +
                    $formatNumber(record.amount_due, 2)
                  : 0
              }}
            </td>
            <td class="ts-text-center">
              <span class="badge badge-success">
                {{ record.expenses_count }}
              </span>
            </td>
            <td class="ts-text-center">{{ record.vendor_name }}</td>
            <td>{{ record.description }}</td>
            <td>{{ record.create_by ? record.create_by.name : '' }}</td>
            <td class="text-center ts-space-x-3 ts-whitespace-no-wrap">
              <a
                href="#"
                v-tooltip="$t('dailyCashAdvance.reprint')"
                class="text-primary text-red"
                @click.prevent="reprintReport(record)"
              >
                <i class="fas fa-file-pdf"></i>
              </a>
              <a
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(record)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(record)"
              >
                <a href="#" :disabled="record._deleting">
                  <i
                    class="fas fa-circle-notch spin"
                    v-if="record._deleting"
                  ></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
      <!-- form-action -->
      <a-modal
        v-if="showForm"
        v-model="showForm"
        :title="$t('dailyCashAdvance.pageTitle')"
        centered
        width="30%"
        @cancel="clearEdit"
        :maskClosable="false"
        :zIndex="1020"
        :footer="null"
      >
        <from-action @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
      <a-modal :title="$t('pdf')" width="50%" v-model="show_pdf" :footer="null">
        <ts-preview-pdf v-model="waiting" :src="src" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'
import { Errors } from 'form-backend-validation'
import Reason from './reason_delete'

export default {
  name: 'DailyCashAdvance',
  components: {
    FromAction,
    Reason
  },
  data() {
    return {
      loading: false,
      showForm: false,
      waiting: false,
      show_pdf: false,
      src: '',
      errors: new Errors(),
      model: {
        deleted_reason: null
      }
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('expense/dailyCashAdvance', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.expense.dailyCashAdvance.search
      },
      set(newValue) {
        this.$store.commit('expense/dailyCashAdvance/SET_SEARCH', newValue)
        this.$store.commit('expense/dailyCashAdvance/RESET_CURRENT_PAGE')
      }
    },
    dateRange: {
      get() {
        return this.$store.state.expense.dailyCashAdvance.dateRange
      },
      set(newValue) {
        this.$store.commit('expense/dailyCashAdvance/SET_DATE_RANGE', newValue)
      }
    },
    columns() {
      return [
        { name: this.$t('dailyCashAdvance.branch') },
        { name: this.$t('dailyCashAdvance.advancedBy') },
        { name: this.$t('dailyCashAdvance.purpose') },
        { name: this.$t('dailyCashAdvance.paymentMethod') },
        {
          name: this.$t('dailyCashAdvance.advancedAmount'),
          style: 'text-align:right'
        },
        {
          name: this.$t('dailyCashAdvance.amountUsed'),
          style: 'text-align:right'
        },
        {
          name: this.$t('dailyCashAdvance.amountDue'),
          style: 'text-align:right'
        },
        {
          name: this.$t('dailyCashAdvance.expense'),
          style: 'text-align:right'
        },
        {
          name: this.$t('dailyCashAdvance.vendorName'),
          style: 'text-align:center'
        },
        {
          name: this.$t('dailyCashAdvance.description')
        },
        {
          name: this.$t('dailyCashAdvance.createdBy')
        },
        { name: this.$t('actions'), style: 'text-align:center; width: 1px;' }
      ]
    }
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('expense/dailyCashAdvance/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.showForm = true
    },
    edit(record) {
      this.$store.commit('expense/dailyCashAdvance/SET_EDIT_DATA', record)
      this.showForm = true
    },
    clearEdit() {
      this.$store.commit('expense/dailyCashAdvance/SET_EDIT_DATA', {})
      this.showForm = false
      this.fetchData()
    },
    deleteRecord(record) {
      this.model.deleted_reason = null
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        // content: h =>
        //   h(Reason, {
        //     props: {
        //       value: this.model,
        //       validate: this.errors,
        //       placeholder: this.$t('expense.leaveReason')
        //     }
        //   }),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        onOk: () => {
          return this.$store
            .dispatch('expense/dailyCashAdvance/destroy', {
              id: record.advance_id,
              data: this.model
            })
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              this.errors = new Errors(error.errors)
              return Promise.reject(error)
            })
        },
        onCancel() {}
      })
    },
    branchFilter(value) {
      this.$store.commit('expense/dailyCashAdvance/SET_BRANCH_ID', value)
      this.fetchData()
    },
    reprintReport(record) {
      this.show_pdf = true
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('expense/dailyCashAdvance/toPdf', record.advance_id)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('expense/dailyCashAdvance/RESET_STATE')
    next()
  }
}
</script>
